export const fears = [
  {
    slug: "darkness",
    name: "Fear of Darkness",
    roomName: "The Room of Darkness",
    category: "Unknown",
    intensity: 6,
    tagline: "Fear grows where the mind has to finish the picture.",
    coreQuestion: "What might be there that I cannot see?",
    commonTrigger: "Unlit spaces, night, silence",
    atmosphere: "darkness",
    shortDescription: "A fear of the unseen space between what is known and what must be imagined.",
    whatItFeelsLike: "Darkness can make a familiar room feel unfinished. The edges soften, ordinary objects lose their names, and the mind begins adding possibilities where vision stops.",
    whyItExists: "Fear often works as a protective signal. When information is missing, the mind tries to predict danger early, even if the danger is only an outline.",
    commonSigns: ["avoidance", "tension", "overthinking", "racing thoughts", "physical unease", "checking behavior"],
    reflectionQuestions: ["When did you first notice this fear?", "What does it try to protect you from?", "What would this fear say if it had a voice?", "What becomes possible when it gets quieter?"],
    quote: "Fear is not always an enemy. Sometimes it is a guard who forgot when to stand down."
  },
  {
    slug: "depth",
    name: "Fear of Depth",
    roomName: "The Room Below",
    category: "Physical",
    intensity: 7,
    tagline: "The body remembers that some places do not forgive mistakes.",
    coreQuestion: "What is beneath me?",
    commonTrigger: "Deep water, heights, abysses",
    atmosphere: "depth",
    shortDescription: "A physical alarm that appears around distance, scale, and the pull of what cannot be reached.",
    whatItFeelsLike: "Depth can feel like a place with its own gravity. The body tightens before thought explains why, as if distance itself has become a voice.",
    whyItExists: "The body is built to notice edges, falls, and environments where escape may be difficult. This fear can be a fast calculation about risk and vulnerability.",
    commonSigns: ["freezing", "tension", "avoidance", "physical unease", "racing thoughts"],
    reflectionQuestions: ["Where does this fear live in your body?", "What does distance seem to mean in this room?", "What helps you feel oriented?", "What becomes possible when it gets quieter?"],
    quote: "Some fears begin in thought. Others begin in the knees."
  },
  {
    slug: "failure",
    name: "Fear of Failure",
    roomName: "The Room of Unfinished Attempts",
    category: "Failure",
    intensity: 8,
    tagline: "Sometimes the scariest thing is being seen trying.",
    coreQuestion: "Who am I if I fail?",
    commonTrigger: "Exams, work, public goals, judgment",
    atmosphere: "failure",
    shortDescription: "A fear that turns effort into exposure and makes possibility feel like a verdict.",
    whatItFeelsLike: "Failure can make the future feel already judged. Before anything happens, the mind rehearses disappointment and calls it preparation.",
    whyItExists: "Belonging, competence, and safety can become tied to performance. The mind may try to avoid pain by avoiding the attempt itself.",
    commonSigns: ["avoidance", "overthinking", "need for control", "checking behavior", "tension"],
    reflectionQuestions: ["What would trying mean if no one saw the outcome?", "Whose judgment feels present here?", "What standard has become too heavy?", "What becomes possible when it gets quieter?"],
    quote: "A failed attempt is not a portrait of a person. It is evidence of movement."
  },
  {
    slug: "rejection",
    name: "Fear of Rejection",
    roomName: "The Room of Closed Doors",
    category: "Social",
    intensity: 8,
    tagline: "A closed door can sound louder than an insult.",
    coreQuestion: "What if I am not wanted?",
    commonTrigger: "Messages left unread, confession, social risk",
    atmosphere: "abandonment",
    shortDescription: "A social fear that turns uncertain distance into a story about worth.",
    whatItFeelsLike: "Rejection can make a pause feel personal. A silence becomes evidence, a delayed answer becomes a verdict, and the self starts negotiating for permission to stay.",
    whyItExists: "Humans are social beings. The mind often treats connection as safety, so threats to belonging can feel larger than the moment itself.",
    commonSigns: ["overthinking", "avoidance", "racing thoughts", "physical unease", "checking behavior"],
    reflectionQuestions: ["What does this fear assume about your worth?", "What evidence does it ignore?", "What kind of closeness feels safe?", "What becomes possible when it gets quieter?"],
    quote: "Not every closed door is a sentence. Some are only doors."
  },
  {
    slug: "forgotten",
    name: "Fear of Being Forgotten",
    roomName: "The Room Without Photographs",
    category: "Existential",
    intensity: 7,
    tagline: "To disappear from memory feels like a second death.",
    coreQuestion: "Will anything remain of me?",
    commonTrigger: "Aging, distance, lost relationships",
    atmosphere: "loss",
    shortDescription: "A fear about legacy, memory, and the quiet wish to have mattered.",
    whatItFeelsLike: "Being forgotten can feel like fading while still present. It asks whether love survives distance and whether a life leaves any shape behind.",
    whyItExists: "Memory is one way people experience continuity. This fear may point to a need for connection, meaning, and evidence that presence has mattered.",
    commonSigns: ["sadness", "overthinking", "checking behavior", "physical unease", "withdrawal"],
    reflectionQuestions: ["What kind of remembrance matters to you?", "Where are you already leaving traces?", "Who has shaped you without knowing it?", "What becomes possible when it gets quieter?"],
    quote: "To be remembered is tender. To be present is also enough."
  },
  {
    slug: "time",
    name: "Fear of Time",
    roomName: "The Room of Ticking Walls",
    category: "Existential",
    intensity: 9,
    tagline: "Time does not chase. It simply does not stop.",
    coreQuestion: "Am I wasting my life?",
    commonTrigger: "Birthdays, deadlines, old photos",
    atmosphere: "time",
    shortDescription: "A fear of passing days, closing windows, and the pressure to make a life count.",
    whatItFeelsLike: "Time can feel less like a measure and more like a witness. It makes ordinary choices feel urgent and turns comparison into a clock.",
    whyItExists: "Awareness of time helps people prioritize, plan, and care. Under stress, that awareness can become a constant audit of what has not yet happened.",
    commonSigns: ["racing thoughts", "overthinking", "need for control", "tension", "physical unease"],
    reflectionQuestions: ["What does this fear say you are running out of?", "Which parts of life feel borrowed?", "What pace actually belongs to you?", "What becomes possible when it gets quieter?"],
    quote: "Time is not only what takes things away. It is also where things become real."
  },
  {
    slug: "silence",
    name: "Fear of Silence",
    roomName: "The Room That Does Not Answer",
    category: "Isolation",
    intensity: 5,
    tagline: "Silence can make the mind speak too loudly.",
    coreQuestion: "What appears when nothing distracts me?",
    commonTrigger: "Empty rooms, night, no notifications",
    atmosphere: "silence",
    shortDescription: "A fear of stillness, empty space, and thoughts that arrive without invitation.",
    whatItFeelsLike: "Silence can feel crowded from the inside. Without external sound, unfinished thoughts become easier to hear.",
    whyItExists: "Quiet removes distraction. For some people, that makes unresolved stress more noticeable, and the mind may try to escape by filling the room.",
    commonSigns: ["avoidance", "racing thoughts", "physical unease", "overthinking"],
    reflectionQuestions: ["What does silence tend to reveal?", "Which distractions feel protective?", "What kind of quiet feels gentle?", "What becomes possible when it gets quieter?"],
    quote: "Silence is not empty. It often contains what has been waiting."
  },
  {
    slug: "crowds",
    name: "Fear of Crowds",
    roomName: "The Room of Too Many Faces",
    category: "Social",
    intensity: 6,
    tagline: "A crowd can make the self feel smaller.",
    coreQuestion: "Can I stay myself among everyone else?",
    commonTrigger: "Public transport, concerts, busy streets",
    atmosphere: "crowd",
    shortDescription: "A fear of density, attention, and losing orientation inside too much human presence.",
    whatItFeelsLike: "Crowds can make the body scan for exits before the mind has named a problem. Faces multiply, paths narrow, and privacy becomes hard to find.",
    whyItExists: "The nervous system tracks space, movement, and social cues. Crowds can overload those signals and create a need for distance or control.",
    commonSigns: ["tension", "avoidance", "physical unease", "freezing", "checking behavior"],
    reflectionQuestions: ["What does a crowd seem to take from you?", "Where do you look for safety?", "How much space helps you stay present?", "What becomes possible when it gets quieter?"],
    quote: "A crowd can be many things at once: ordinary, alive, and too much."
  },
  {
    slug: "control",
    name: "Fear of Losing Control",
    roomName: "The Room of Locked Switches",
    category: "Control",
    intensity: 8,
    tagline: "Control promises safety, but asks for everything.",
    coreQuestion: "What happens if I cannot hold it together?",
    commonTrigger: "Uncertainty, chaos, waiting, dependency",
    atmosphere: "control",
    shortDescription: "A fear that equates uncertainty with danger and effort with survival.",
    whatItFeelsLike: "Losing control can feel like standing beside a panel of switches that all matter. The mind keeps checking because missing one thing feels unacceptable.",
    whyItExists: "Control can reduce uncertainty and create a sense of preparedness. The difficulty begins when every unknown starts to feel like a threat.",
    commonSigns: ["need for control", "checking behavior", "tension", "overthinking", "avoidance"],
    reflectionQuestions: ["What does control promise you?", "Which unknowns feel hardest to allow?", "What remains steady without constant checking?", "What becomes possible when it gets quieter?"],
    quote: "Control can build a room so secure that no one can breathe inside it."
  },
  {
    slug: "death",
    name: "Fear of Death",
    roomName: "The Room at the End",
    category: "Existential",
    intensity: 10,
    tagline: "The oldest fear has no need to shout.",
    coreQuestion: "What does it mean that everything ends?",
    commonTrigger: "Illness, grief, night thoughts, mortality",
    atmosphere: "death",
    shortDescription: "An ancient fear of ending, separation, and the limit that gives life its shape.",
    whatItFeelsLike: "Death can make the mind stop at a horizon it cannot cross. It is not always panic; sometimes it is a quiet pressure behind ordinary days.",
    whyItExists: "Awareness of mortality is part of being human. It can sharpen care, meaning, and attachment, while also stirring fear when the mind tries to solve what cannot be fully known.",
    commonSigns: ["racing thoughts", "physical unease", "overthinking", "avoidance", "freezing"],
    reflectionQuestions: ["What does this fear make more precious?", "When does it grow loudest?", "What meaning does it ask you to protect?", "What becomes possible when it gets quieter?"],
    quote: "The end is difficult to face. It is also why presence matters."
  },
  {
    slug: "mirrors",
    name: "Fear of Mirrors",
    roomName: "The Room of Reflections",
    category: "Unknown",
    intensity: 5,
    tagline: "A mirror is harmless until it feels like it knows more than you.",
    coreQuestion: "What if I do not recognize myself?",
    commonTrigger: "Reflections at night, self-image, old photos",
    atmosphere: "mirror",
    shortDescription: "A fear of recognition becoming strange and the self appearing slightly unfamiliar.",
    whatItFeelsLike: "Mirrors can turn observation into confrontation. A familiar face can feel distant for a moment, as if identity has moved a step away.",
    whyItExists: "Reflection invites self-awareness. In certain moods, fatigue, or stress, that awareness may feel intensified or uneasy rather than neutral.",
    commonSigns: ["avoidance", "tension", "overthinking", "physical unease"],
    reflectionQuestions: ["What do you expect the mirror to confirm?", "Which version of yourself feels hardest to meet?", "What would gentler seeing change?", "What becomes possible when it gets quieter?"],
    quote: "A reflection shows a surface. The mind supplies the story."
  },
  {
    slug: "abandonment",
    name: "Fear of Abandonment",
    roomName: "The Room of Empty Chairs",
    category: "Loss",
    intensity: 9,
    tagline: "Absence can feel like proof, even when it is not.",
    coreQuestion: "What if everyone eventually leaves?",
    commonTrigger: "Distance, delayed replies, conflict",
    atmosphere: "abandonment",
    shortDescription: "A fear that turns distance into danger and closeness into something that must be protected.",
    whatItFeelsLike: "Abandonment can make ordinary separation feel charged. The empty chair becomes a message, even before anyone has spoken.",
    whyItExists: "Attachment helps people seek safety and care. When past or imagined loss feels close, the mind may scan for signs of leaving before they are clear.",
    commonSigns: ["checking behavior", "racing thoughts", "physical unease", "need for control", "overthinking"],
    reflectionQuestions: ["What does absence seem to prove?", "How do you ask for reassurance?", "What kind of closeness can survive space?", "What becomes possible when it gets quieter?"],
    quote: "Absence is a fact. The meaning attached to it is often a wound speaking."
  },
  {
    slug: "change",
    name: "Fear of Change",
    roomName: "The Room That Rearranges Itself",
    category: "Control",
    intensity: 7,
    tagline: "Even a better future can feel threatening when it is unknown.",
    coreQuestion: "Who will I become if this changes?",
    commonTrigger: "Moving, endings, new roles, uncertainty",
    atmosphere: "unknown",
    shortDescription: "A fear of transition, identity shift, and the loss hidden inside beginnings.",
    whatItFeelsLike: "Change can feel like a room moving while you are still inside it. Even welcome change can unsettle the parts of you that learned safety through familiarity.",
    whyItExists: "Predictability conserves energy and helps people feel oriented. Change asks the mind to rebuild its map before the new landscape feels trustworthy.",
    commonSigns: ["avoidance", "need for control", "overthinking", "tension", "racing thoughts"],
    reflectionQuestions: ["What part of the old room are you trying to keep?", "What does change ask you to trust?", "Who might you become with time?", "What becomes possible when it gets quieter?"],
    quote: "A new room can still feel unsafe before it becomes familiar."
  },
  {
    slug: "illness",
    name: "Fear of Illness",
    roomName: "The Room of Small Symptoms",
    category: "Physical",
    intensity: 8,
    tagline: "The body whispers, and the mind sometimes translates it as danger.",
    coreQuestion: "What if something is wrong with me?",
    commonTrigger: "Pain, symptoms, medical waiting, search results",
    atmosphere: "body",
    shortDescription: "A fear that listens closely to the body and can turn uncertainty into alarm.",
    whatItFeelsLike: "Illness fear can make the body feel like a coded message. A sensation appears, and the mind starts searching for certainty faster than calm can arrive.",
    whyItExists: "Attention to bodily signals can be protective. Under stress, that attention may become heightened, especially when answers are delayed or unclear.",
    commonSigns: ["checking behavior", "racing thoughts", "physical unease", "overthinking", "need for control"],
    reflectionQuestions: ["What does certainty promise in this room?", "How do you respond to not knowing yet?", "What helps you separate care from alarm?", "What becomes possible when it gets quieter?"],
    quote: "The body deserves attention. Fear does not have to interpret every signal alone."
  },
  {
    slug: "humiliation",
    name: "Fear of Humiliation",
    roomName: "The Room of Watching Eyes",
    category: "Social",
    intensity: 8,
    tagline: "Shame turns a moment into a monument.",
    coreQuestion: "What if they remember this forever?",
    commonTrigger: "Public mistakes, social exposure, criticism",
    atmosphere: "crowd",
    shortDescription: "A fear of being reduced to one visible mistake in the minds of others.",
    whatItFeelsLike: "Humiliation can make time slow around a single moment. The mind imagines witnesses replaying it long after everyone has moved on.",
    whyItExists: "Social belonging matters, so the mind monitors signals of status, acceptance, and exposure. Shame can become an attempt to prevent future pain.",
    commonSigns: ["avoidance", "overthinking", "racing thoughts", "tension", "physical unease"],
    reflectionQuestions: ["What moment does this fear keep replaying?", "What does it assume others remember?", "How would you speak to someone else in the same place?", "What becomes possible when it gets quieter?"],
    quote: "Shame edits the room until only the mistake remains."
  },
  {
    slug: "unknown",
    name: "Fear of the Unknown",
    roomName: "The Room Behind the Door",
    category: "Unknown",
    intensity: 7,
    tagline: "The unknown is not empty. It is filled by imagination.",
    coreQuestion: "What if I am not ready for what comes next?",
    commonTrigger: "Waiting, transitions, unclear outcomes",
    atmosphere: "unknown",
    shortDescription: "A fear of unclear outcomes and the imagination that rushes to fill them.",
    whatItFeelsLike: "The unknown can feel like a closed door with sound behind it. Nothing has happened yet, but the mind begins preparing for several futures at once.",
    whyItExists: "Prediction is one way the mind tries to keep people safe. When there is not enough information, imagination may become louder than evidence.",
    commonSigns: ["overthinking", "need for control", "racing thoughts", "avoidance", "physical unease"],
    reflectionQuestions: ["What future is your mind rehearsing?", "What do you know for certain right now?", "What would readiness actually require?", "What becomes possible when it gets quieter?"],
    quote: "The unknown is a door, not a verdict."
  }
];

export const categories = ["All", "Social", "Existential", "Physical", "Control", "Unknown", "Isolation", "Failure", "Loss"];
